import React from 'react';
import { Box } from '@mui/material';
import {
    Patient,
    PatientTask,
    TabKey,
    WorkListTabConfig,
    WorklistPatient,
} from './types';

export const poppins = 'Poppins, sans-serif';

export const getIconPathForLabel = (label: string) => {
    switch (label) {
        case 'İlaç Tedavileri': return '/assets/icons/work-list/work-list-order-medicine.svg?v=2';
        case 'Diğer Tedaviler': return '/assets/icons/work-list/work-list-other-medicine.svg?v=2';
        case 'Bakım Planları': return '/assets/icons/work-list/care-plans.svg?v=2';
        case 'Kan İstekleri': return '/assets/icons/work-list/blond-request.svg?v=2';
        case 'Ameliyathane': return '/assets/icons/work-list/operating-room.svg?v=2';
        case 'Tetkik Hazırlık': return '/assets/icons/work-list/Radyoloji.svg?v=2';
        case 'Hizmet İsteği': return '/assets/icons/work-list/service-request.svg?v=2';
        case 'Hasta Devir': return '/assets/icons/work-list/patient-handover.svg?v=2';
        case 'Plan Bekleyen': return '/assets/icons/work-list/waiting-plan.svg?v=2';
        default: return '/assets/icons/work-list/work-list-other-medicine.svg?v=2';
    }
};

const mapTypeIdToTypeAndLabel = (typeId: number): { type: PatientTask['type']; label: string } => {
    switch (typeId) {
        case 1: return { type: 'medicine', label: 'İlaç Tedavileri' };
        case 2: return { type: 'digerTedavi', label: 'Diğer Tedaviler' };
        case 3: return { type: 'other', label: 'Kan İstekleri' };
        case 4: return { type: 'tetkik', label: 'Tetkik Hazırlık' };
        case 5: return { type: 'surgery', label: 'Ameliyathane' };
        case 6: return { type: 'other', label: 'Bakım Planları' };
        case 7: return { type: 'other', label: 'Hizmet İsteği' };
        case 8: return { type: 'other', label: 'Hasta Devir' };
        case 9: return { type: 'other', label: 'Plan Bekleyen' };
        default: return { type: 'other', label: 'Diğer Tedaviler' };
    }
};

const mapStatusIdToStatus = (statusId: number, typeTimeIso: string): PatientTask['status'] => {
    if (statusId === 110) return 'applied';

    const date = typeTimeIso ? new Date(typeTimeIso) : null;
    if (date && !isNaN(date.getTime()) && date.getTime() < Date.now()) {
        return 'expired';
    }

    return 'pending';
};

const mapActionTypeFromIds = (typeId: number, statusId: number): PatientTask['actionType'] => {
    if (typeId === 4) {
        if (statusId === 402) return 'tetkikGonder';
        if (statusId === 403) return 'tetkikKabul';
    }

    if (typeId === 5) {
        if (statusId === 503) return 'gonder';
        if (statusId === 501) return 'uygula';
        if (statusId === 502) return 'tetkikKabul';
    }

    return 'uygula';
};

const formatTimeFromIso = (iso: string | null | undefined): string => {
    if (!iso) return '';
    const match = iso.match(/T(\d{2}:\d{2})/);
    return match ? match[1] : '';
};

export const normalizeForCompare = (value: string | null | undefined): string =>
    (value ?? '').trim().toLocaleLowerCase('tr');

export const normalizeApiText = (value: unknown): string => {
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value).trim();
    return '';
};

export const matchesTabFilter = (task: PatientTask, tab: TabKey): boolean => {
    if (tab === 'all') return true;
    if (tab === 'expired' && task.label === 'Tetkik Hazırlık') return false;
    return task.status === tab;
};

export const mapWorklistPatientsToUi = (apiPatients: WorklistPatient[]): Patient[] => {
    return apiPatients.map((p) => {
        const tasks: PatientTask[] = (p.items || []).map((item) => {
            const { type, label } = mapTypeIdToTypeAndLabel(item.typeid);
            return {
                id: String(item.id),
                type,
                label,
                time: formatTimeFromIso(item.typetime),
                status: mapStatusIdToStatus(item.statusid, item.typetime),
                actionType: mapActionTypeFromIds(item.typeid, item.statusid),
                typeId: item.typeid,
                statusId: item.statusid,
            };
        });

        return {
            id: String(p.ptid),
            name: p.name,
            iconCount: p.count ?? tasks.length,
            isFavorite: Boolean(p.isfavorite),
            tasks,
        };
    });
};

export const getActionLabel = (task: PatientTask): string => {
    if (task.actionType === 'tetkikGonder') return 'Tetkik İçin Gönder';
    if (task.actionType === 'tetkikKabul') return 'Servise Kabul Et';
    if (task.actionType === 'gonder' || task.type === 'surgery') return 'Ameliyathaneye Gönder';
    if (task.actionType === 'digerTedaviUygula') return 'Uygula';
    return 'Uygula';
};

export const getActionBtnColor = (task: PatientTask): { bg: string; border: string; text: string } => {
    if (task.status === 'applied') {
        return { bg: '#AEADA633', border: '#AEADA6', text: '#AEADA6' };
    }
    if (task.actionType === 'tetkikGonder' || task.actionType === 'tetkikKabul') {
        return { bg: 'rgba(33, 150, 243, 0.15)', border: '#2196F3', text: '#2196F3' };
    }
    if (task.type === 'surgery' || task.actionType === 'gonder') {
        return { bg: 'rgba(33, 150, 243, 0.15)', border: '#2196F3', text: '#2196F3' };
    }
    return { bg: 'rgba(99, 180, 255, 0.20)', border: '#2979C3', text: '#2979C3' };
};

const activeFilter = 'brightness(0) saturate(100%) invert(48%) sepia(87%) saturate(2250%) hue-rotate(190deg) brightness(97%) contrast(95%)';

const IconHourglass: React.FC<{ active?: boolean }> = ({ active }) => (
    <Box component="img" src="/assets/icons/work-list/waiting.svg" alt="Pending" sx={{ width: 20, height: 20, transition: 'filter 0.2s', filter: active ? activeFilter : 'none' }} />
);

const IconAlert: React.FC<{ active?: boolean }> = ({ active }) => (
    <Box component="img" src="/assets/icons/work-list/expired.svg" alt="Expired" sx={{ width: 20, height: 20, transition: 'filter 0.2s', filter: active ? activeFilter : 'none' }} />
);

const IconCheck: React.FC<{ active?: boolean }> = ({ active }) => (
    <Box component="img" src="/assets/icons/work-list/accepted.svg" alt="Applied" sx={{ width: 20, height: 20, transition: 'filter 0.2s', filter: active ? activeFilter : 'none' }} />
);

const IconGrid: React.FC<{ active?: boolean }> = ({ active }) => (
    <Box component="img" src="/assets/icons/work-list/Category.svg" alt="All" sx={{ width: 20, height: 20, transition: 'filter 0.2s', filter: active ? activeFilter : 'none' }} />
);

export const WORKLIST_TABS: WorkListTabConfig[] = [
    { key: 'pending', label: 'Bekleyen İşler', icon: (active) => <IconHourglass active={active} />, badges: [{ count: 14, color: '#2196F3' }] },
    { key: 'expired', label: 'Süresi Geçen İşler', icon: (active) => <IconAlert active={active} />, badges: [{ count: 10, color: '#CA0003' }] },
    { key: 'applied', label: 'Uygulanan İşler', icon: (active) => <IconCheck active={active} />, badges: [{ count: 2, color: '#0A773B' }] },
    {
        key: 'all',
        label: 'Tüm İşler',
        icon: (active) => <IconGrid active={active} />,
        badges: [
            { count: 14, color: '#2196F3' },
            { count: 10, color: '#CA0003' },
            { count: 2, color: '#0A773B' },
        ],
    },
];
